import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LineItem, Order } from '@infrab4a/connect';
import { ConfirmationService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { ShopOrderService } from 'src/app/connect-api/api/shop/shop-order.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface RefundUser {
  id: string;
  name: string;
  email: string;
  origin?: string;
}

@Component({
  selector: 'app-shop-order-shopping-refund-modal',
  templateUrl: './shop-order-shopping-refund-modal.component.html',
  styleUrl: './shop-order-shopping-refund-modal.component.scss',
  providers: [ConfirmationService]
})
export class ShopOrderShoppingRefundModalComponent implements OnInit {
  order: Order = inject(DynamicDialogConfig).data.order;
  fullRefund: boolean = inject(DynamicDialogConfig).data.fullRefund;
  lineItems: LineItem[] = inject(DynamicDialogConfig).data.lineItems;
  user: RefundUser = inject(DynamicDialogConfig).data.user;
  ref = inject(DynamicDialogRef);
  private readonly shopOrderService = inject(ShopOrderService);
  private readonly confirmationService = inject(ConfirmationService);

  selectedEans = new Set<string>();

  form = new FormGroup({
    motivo: new FormControl<string>(null, [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(200)
    ]),
    shouldRefund: new FormControl<boolean>(true),
    shouldResend: new FormControl<boolean>(false)
  });

  ngOnInit(): void {
    if (!this.fullRefund) {
      this.lineItems.forEach((item) => this.selectedEans.add(item.EAN));
    }
  }

  toggleProduct(ean: string, checked: boolean): void {
    if (checked) {
      this.selectedEans.add(ean);
    } else {
      this.selectedEans.delete(ean);
    }
  }

  isSelected(ean: string): boolean {
    return this.selectedEans.has(ean);
  }

  getProductImage(item: LineItem): string {
    return (
      item.image || item.images?.at(0) || 'assets/images/no_img_available.jpg'
    );
  }

  get canSubmit(): boolean {
    if (!this.form.valid) return false;
    if (!this.fullRefund && this.selectedEans.size === 0) return false;
    return true;
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) return;

    const shouldRefund = !!this.form.value.shouldRefund;
    const shouldResend = !this.fullRefund && !!this.form.value.shouldResend;

    if (shouldRefund || shouldResend) {
      this.confirmationService.confirm({
        acceptLabel: 'Confirmar',
        rejectLabel: 'Voltar',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        header: 'Confirmar ação',
        message: this.buildConfirmationMessage(shouldRefund, shouldResend),
        accept: () => this.executeSubmit()
      });
      return;
    }

    await this.executeSubmit();
  }

  buildConfirmationMessage(
    shouldRefund: boolean,
    shouldResend: boolean
  ): string {
    if (shouldRefund && shouldResend) {
      return 'Deseja confirmar o cancelamento com estorno e reenvio?';
    }
    if (shouldRefund) {
      return 'Deseja confirmar o cancelamento com estorno?';
    }
    return 'Deseja confirmar o cancelamento com reenvio?';
  }

  async executeSubmit(): Promise<void> {
    if (!this.canSubmit) return;

    const motivo = this.form.value.motivo;
    const shouldRefund = this.form.value.shouldRefund;
    const shouldResend = this.fullRefund
      ? undefined
      : this.form.value.shouldResend;
    let apiFullRefund = this.fullRefund;
    let items: string[] | undefined;

    if (this.fullRefund) {
      apiFullRefund = true;
      items = undefined;
    } else {
      const selected = Array.from(this.selectedEans);
      apiFullRefund = selected.length === this.lineItems.length;
      items = apiFullRefund ? undefined : selected;
    }

    try {
      LoaderService.showLoader();
      const response = await lastValueFrom(
        this.shopOrderService.refundOrder(
          this.order.orderNumber,
          items,
          apiFullRefund,
          motivo,
          this.user,
          shouldRefund,
          shouldResend
        )
      );
      this.ref.close(response);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
